'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

interface Trailer {
  id: string;
  name: string;
  capacity: string;
  length: string;
  width: string;
  tare: string;
  max_load: string;
  available: boolean;
  img: string;
  alt: string;
  description: string;
  features: string[];
}

export default function TrailerGrid() {
  const [trailers, setTrailers] = useState<Trailer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTrailers = async () => {
      try {
        const supabase = createClient();
        const { data, error: fetchError } = await supabase
          .from('trailers')
          .select('*')
          .order('sort_order', { ascending: true });

        if (fetchError) throw fetchError;
        setTrailers(data || []);
      } catch (err: any) {
        setError('Greška pri učitavanju prikolica.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTrailers();
  }, []);

  if (loading) {
    return (
      <section className="section-padding px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="section-padding px-6">
        <div className="max-w-7xl mx-auto py-20 text-center text-muted-foreground text-sm">{error}</div>
      </section>
    );
  }

  if (trailers.length === 0) {
    return (
      <section className="section-padding px-6">
        <div className="max-w-7xl mx-auto py-20 text-center text-muted-foreground text-sm">
          Trenutno nema dostupnih prikolica. Provjerite uskoro.
        </div>
      </section>
    );
  }

  return (
    <section className="section-padding px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-primary mb-3 block">// Naša flota</span>
            <h2 className="text-section-title text-foreground fade-up-init">
              Dostupne <span className="text-primary">prikolice.</span>
            </h2>
          </div>
          <p className="text-muted-foreground text-sm max-w-xs fade-up-init stagger-2">
            Rezervišite odmah — pozovite nas ili pošaljite upit putem forme ispod.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {trailers.map((trailer, i) => (
            <TrailerCard key={trailer.id} trailer={trailer} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TrailerCard({ trailer, index }: { trailer: Trailer; index: number }) {
  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = '/assets/images/no_image.png';
  };

  return (
    <div className={`bg-card border border-border rounded-sm overflow-hidden group card-lift fade-up-init stagger-${index + 1}`}>
      {/* Image */}
      <div className="relative overflow-hidden" style={{ height: '240px' }}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={trailer.img || '/assets/images/no_image.png'}
          alt={trailer.alt || trailer.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
          onError={handleImgError}
        />

        {/* Availability badge */}
        <span
          className={`absolute top-3 left-3 text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-sm ${
            trailer.available
              ? 'bg-green-600/90 text-white' :'bg-muted/90 text-muted-foreground'
          }`}
        >
          {trailer.available ? 'Dostupno' : 'Zauzeto'}
        </span>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-foreground font-bold text-xl mb-2">{trailer.name}</h3>
        <p className="text-muted-foreground text-sm leading-relaxed mb-5">{trailer.description}</p>

        {/* Specs grid */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          {[
            { label: 'Nosivost', value: trailer.capacity },
            { label: 'Dužina', value: trailer.length },
            { label: 'Širina', value: trailer.width },
            { label: 'Tara', value: trailer.tare },
          ].map((spec) => (
            <div key={spec.label} className="bg-secondary/50 border border-border rounded-sm px-3 py-2">
              <span className="text-muted-foreground text-xs block leading-none mb-1">{spec.label}</span>
              <span className="text-foreground text-xs font-bold">{spec.value}</span>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="flex flex-wrap gap-2 mb-5">
          {trailer.features?.map((f) => (
            <span key={f} className="text-xs bg-primary/10 border border-primary/20 text-primary px-2 py-1 rounded-sm font-medium">
              {f}
            </span>
          ))}
        </div>

        {/* CTA */}
        <Link
          href="/#kontakt"
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-sm text-sm font-bold uppercase tracking-wide transition-colors duration-300 ${
            trailer.available
              ? 'bg-primary text-primary-foreground hover:bg-accent'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
        >
          {trailer.available ? 'Rezervišite' : 'Trenutno zauzeto'}
          {trailer.available && (
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14m-7-7 7 7-7 7" />
            </svg>
          )}
        </Link>
      </div>
    </div>
  );
}